// Reports view — "Daily macro breakdown (logged days)".
// One stacked bar per logged day (Protein/Carbs/Fat, each converted to its
// own kcal contribution — see buildMacroBreakdownSeries's macroKcals — so the
// 3 segments stack to the same kcal axis the Target line is drawn on) plus a
// trailing "Avg" bar. The tooltip title/rows reuse nutritionPage.chart.* —
// the SAME copy the legacy AdherenceChartNew.js tooltip already uses for this
// exact "Macros / Protein Xg / Carbs Xg / Fat Xg / Consumed kCal / Target
// kCal" shape, so this isn't a second English string to keep in sync.

import React, { useMemo } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

import { MACRO_COLORS, translateMacroLabel, translateMacroUnit, CHART_THEME } from "./viewTokens";
import { ChartLegend, TooltipShell, tdeeReferenceLineProps } from "./viewAtoms";
import { fmtNum } from "./TableFormat";
import { useCenteredTooltip } from "../../../../../hooks/useCenteredTooltip";

// TARGET_COLOR is the legend LABEL's text color only. The dashed line itself
// (both on the chart and its legend icon) uses PRIMARY_COLOR instead —
// intentionally different from the label, per design.
const TARGET_COLOR = "var(--white-50)";
const PRIMARY_COLOR = "var(--primary)";
// Theme-aware, shared with ReportsIntakeChart.js/SummaryHero.js/
// ReportsWeekdayChart.js — see CHART_THEME (viewTokens.js) for the full "why".
// No cursor entry needed here: this is a ComposedChart (Bar + Line mixed),
// and Recharts' Tooltip only draws the fill-based Rectangle cursor when
// chartName === "BarChart" — for anything else (including ComposedChart) it
// falls back to a plain vertical Curve/line cursor that ignores `fill`
// entirely, so the fill token never did anything useful here. Disabled via
// cursor={false} below instead (unwanted line, requested by the user).
const { tick: CHART_TICK, tickFontSize: CHART_TICK_SIZE, grid: CHART_GRID } = CHART_THEME;

// Minimum px per bar in the horizontally-scrollable chart — see
// ReportsView.js's comment on why this shows every logged day uncapped.
const MIN_BAR_PX = 64;
// Cap on each bar's width so a few logged days don't stretch into huge
// blocks that fill the whole row (the legacy AdherenceChartNew uses a fixed
// barSize={20}). maxBarSize, not barSize, so a bar can still shrink if the
// column ever gets narrower than this.
const MAX_BAR_PX = 28;

const cm = (t, key, opts) => t(`nutritionPage.chart.${key}`, opts);
const rm = (t, key, opts) => t(`nutritionPage.progress.reports.macroChart.${key}`, opts);

/* ---- custom tooltip ------------------------------------------------------ */

const MacroTooltip = ({ active, payload, onMeasureWidth }) => {
  const { t } = useTranslation("athlete-portal");
  return (
    <TooltipShell active={active} payload={payload} onMeasureWidth={onMeasureWidth} isEmpty={(pt) => !pt}>
      {(pt) => (
        <>
          <div className="np-reports__tooltip-title">{cm(t, "macros")}</div>
          <div className="np-hero__tooltip-row">
            <span style={{ color: MACRO_COLORS.proteins }}>{translateMacroLabel(t, "proteins")}</span>
            <span className="np-reports__tooltip-value">{fmtNum(pt.proteins)}{translateMacroUnit(t, "proteins")}</span>
          </div>
          <div className="np-hero__tooltip-row">
            <span style={{ color: MACRO_COLORS.carbs }}>{translateMacroLabel(t, "carbs")}</span>
            <span className="np-reports__tooltip-value">{fmtNum(pt.carbs)}{translateMacroUnit(t, "carbs")}</span>
          </div>
          <div className="np-hero__tooltip-row">
            <span style={{ color: MACRO_COLORS.fats }}>{translateMacroLabel(t, "fats")}</span>
            <span className="np-reports__tooltip-value">{fmtNum(pt.fats)}{translateMacroUnit(t, "fats")}</span>
          </div>
          <div className="np-hero__tooltip-row">
            <span className="np-reports__tooltip-label">{cm(t, "consumedKcal")}</span>
            <span className="np-reports__tooltip-value">{fmtNum(pt.consumedKcal)}</span>
          </div>
          <div className="np-hero__tooltip-row">
            <span className="np-reports__tooltip-label">{cm(t, "targetKcalLabel")}</span>
            <span className="np-reports__tooltip-value">{fmtNum(pt.targetKcal)}</span>
          </div>
        </>
      )}
    </TooltipShell>
  );
};

MacroTooltip.propTypes = {
  active: PropTypes.bool,
  payload: PropTypes.array,
  onMeasureWidth: PropTypes.func,
};

/* ---- chart ----------------------------------------------------------------- */

export const ReportsMacroChart = ({ series, workingExpenditure, decoupled, staleData, weeklyChange, flush }) => {
  const { t } = useTranslation("athlete-portal");
  // No yAxisId — this chart only has one (default) YAxis, unlike HeroChart's/
  // ReportsIntakeChart's dual-axis "kcal"/"weight" setup.
  const tdeeLineProps = tdeeReferenceLineProps({ workingExpenditure, decoupled, staleData, weeklyChange, t });

  // width: max(100%, N*MIN_BAR_PX) — fills the card when there's room (few
  // days), grows past it and scrolls (via .np-reports__scroll's overflow-x)
  // when there isn't, instead of squeezing every bar to illegibility.
  const chartWidth = useMemo(
    () => `max(100%, ${Math.max(series.length, 1) * MIN_BAR_PX}px)`,
    [series.length]
  );

  // Legend reading order (Protein, Carbs, Fat, Target) is intentionally
  // different from the <Bar> declaration order below (Fat, Carbs, Protein —
  // required for the bottom-to-top stack per the mockup) — Recharts derives
  // the legend from series declaration order by default, so it's overridden
  // here via an explicit payload instead.
  const legendPayload = useMemo(
    () => [
      { value: translateMacroLabel(t, "proteins"), color: MACRO_COLORS.proteins, dataKey: "proteinsKcal" },
      { value: translateMacroLabel(t, "carbs"), color: MACRO_COLORS.carbs, dataKey: "carbsKcal" },
      { value: translateMacroLabel(t, "fats"), color: MACRO_COLORS.fats, dataKey: "fatsKcal" },
      {
        value: rm(t, "legendTarget"),
        color: PRIMARY_COLOR,
        textColor: TARGET_COLOR,
        dataKey: "targetKcal",
        swatchShape: "dashedLine",
      },
    ],
    [t]
  );

  // Manual tooltip centering — see useCenteredTooltip's own header comment
  // for the full "why" (Recharts doesn't do this by default).
  const { containerRef, tooltipPosition, onMeasureWidth, chartHandlers } = useCenteredTooltip();

  return (
    <div className={`np-reports__scroll${flush ? " np-reports__scroll--flush" : ""}`}>
      <div ref={containerRef} style={{ width: chartWidth, height: 280 }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={series} margin={{ top: 8, right: 16, bottom: 0, left: 0 }} {...chartHandlers}>
            <CartesianGrid strokeDasharray="3 3" stroke={CHART_GRID} vertical={false} />
            <XAxis
              dataKey="name"
              tick={{ fontSize: CHART_TICK_SIZE, fill: CHART_TICK }}
              tickLine={false}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: CHART_TICK_SIZE, fill: CHART_TICK }}
              tickLine={false}
              axisLine={false}
              width={44}
            />
            <Tooltip
              content={<MacroTooltip onMeasureWidth={onMeasureWidth} />}
              cursor={false}
              position={tooltipPosition}
            />
            <Legend
              content={<ChartLegend coloredText />}
              payload={legendPayload}
              wrapperStyle={{ paddingTop: "1rem" }}
            />

            {/* Stack order (bottom→top) matches the mockup: Fat, Carbs, Protein —
                Recharts stacks same-stackId bars bottom-up in JSX declaration
                order, so Fat is declared first; only the topmost (Protein)
                gets the rounded top corner. */}
            <Bar name={translateMacroLabel(t, "fats")} dataKey="fatsKcal" stackId="macros" fill={MACRO_COLORS.fats} maxBarSize={MAX_BAR_PX} isAnimationActive={false} />
            <Bar name={translateMacroLabel(t, "carbs")} dataKey="carbsKcal" stackId="macros" fill={MACRO_COLORS.carbs} maxBarSize={MAX_BAR_PX} isAnimationActive={false} />
            <Bar name={translateMacroLabel(t, "proteins")} dataKey="proteinsKcal" stackId="macros" fill={MACRO_COLORS.proteins} radius={[4, 4, 0, 0]} maxBarSize={MAX_BAR_PX} isAnimationActive={false} />

            <Line
              name={rm(t, "legendTarget")}
              dataKey="targetKcal"
              stroke={PRIMARY_COLOR}
              strokeWidth={1.5}
              strokeDasharray="4 2"
              dot={false}
              activeDot={{ r: 4, strokeWidth: 0 }}
              connectNulls
              type="monotone"
              isAnimationActive={false}
            />

            {/* Optional TDEE reference line — only used by Summary's
                chart-switcher ("Stacked macros to target (include TDEE)");
                Reports' own usage never passes workingExpenditure, so this
                renders nothing there. Declared last (not right after the
                Bars) so it paints on top of them — same reasoning as
                HeroChart.js/ReportsIntakeChart.js's own TDEE line. */}
            {tdeeLineProps && <ReferenceLine {...tdeeLineProps} />}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

ReportsMacroChart.propTypes = {
  series: PropTypes.array.isRequired,
  workingExpenditure: PropTypes.number,
  decoupled: PropTypes.bool,
  staleData: PropTypes.bool,
  weeklyChange: PropTypes.number,
  flush: PropTypes.bool,
};
